import type { CardColor } from './card';
import type { Card as CardClass } from '@/core/Card';
import type { Player, Team } from './player';

/**
 * ソロモードでのチーム選択
 * - team1: デュオ側（AIパートナーと協力、持ち点60pt）
 * - team2: ソロ側（1人で2AI相手、持ち点80pt）
 */
export type SoloTeamChoice = 'team1' | 'team2';

/**
 * ドローペナルティの種別
 * Draw2とDraw4を区別するために使用（累積時の回避判定用）
 */
export type DrawPenaltyType = 'DRAW_TWO' | 'WILD_DRAW_FOUR' | null;

/**
 * ゲームフェーズ
 */
export enum GamePhase {
  WAITING = 'waiting',   // プレイヤー待機中
  PLAYING = 'playing',   // ゲーム進行中
  FINISHED = 'finished'  // ゲーム終了
}

/**
 * ゲーム状態インターフェース
 */
export interface GameState {
  /** ゲームフェーズ */
  phase: GamePhase;
  /** プレイヤー一覧 */
  players: Player[];
  /** 現在のプレイヤーインデックス */
  currentPlayerIndex: number;
  /** ターン方向（1: 時計回り、-1: 反時計回り） */
  direction: 1 | -1;
  /** デッキ（山札） */
  deck: CardClass[];
  /** 捨て札 */
  discardPile: CardClass[];
  /** Draw累積カウント（Draw2/WildDraw4の累積） */
  drawCount: number;
  /** ドローペナルティの種別（回避判定用） */
  drawPenaltyType: DrawPenaltyType;
  /** 勝利チーム */
  winner: Team | null;
  /** ワイルドカードで宣言された色 */
  declaredColor?: CardColor;
  /**
   * 現在のターンで既にカードを引いたか
   * 任意ドロー後にプレイ or パスを選べるようにするためのフラグ
   */
  hasDrawnThisTurn?: boolean;

  // ====== 2v1 変則ターン順ルール (issues #47/#48/#49) ======
  /**
   * 犯人（単独チーム）プレイヤーの index。
   * 基本順番ルール: 警察 → 犯人 → 警察 → 犯人 → ... の固定パターン。
   */
  crookIndex: number;
  /**
   * 警察2人の player index。`[COP1_idx, COP2_idx]` の順で正規化される。
   */
  copIndices: number[];
  /**
   * 直近で警察ターンを取った player index。
   * 犯人ターンから次の警察を決める時に「他の警察」を選ぶための参照。
   * 初期化時は `copIndices[0]` (COP1) にしておくことで、
   * SOLO mode の初手 (犯人開始) → 次は COP2 という spec パターンに一致する。
   */
  lastCopIndex: number;
  /**
   * cop alternation flip フラグ。Reverse カードを toggle する。
   * `false`: 通常 (犯人ターン後は lastCopIndex とは違う警察へ)
   * `true`: 反転 (犯人ターン後は lastCopIndex と同じ警察へ)
   * 永続フラグ。次の Reverse まで状態を保持する (消費されない)。
   */
  alternationFlipped: boolean;
}

/**
 * AIアクション型
 */
export interface AIAction {
  /** アクションタイプ */
  type: 'play' | 'draw';
  /** プレイするカードID（type='play'の場合） */
  cardId?: string;
  /** 宣言する色（ワイルドカードの場合） */
  declaredColor?: CardColor;
}

/**
 * マッチフェーズ（複数ラウンドの試合状態）
 */
export enum MatchPhase {
  IN_PROGRESS = 'in_progress',  // 試合進行中
  FINISHED = 'finished'         // 試合終了
}

/**
 * ラウンド結果
 */
export interface RoundResult {
  /** ラウンド番号 */
  roundNumber: number;
  /** ラウンド勝者チーム */
  winner: Team;
  /** プレイヤーごとのダメージ（playerId → damage） */
  damageDealt: Record<string, number>;
  /** プレイヤーごとの残り手札枚数 */
  remainingCards: Record<string, number>;
}

/**
 * マッチ状態（複数ラウンドの試合管理）
 */
export interface MatchState {
  /** マッチフェーズ */
  phase: MatchPhase;
  /** プレイヤーごとの持ち点（playerId → points） */
  lifePoints: Record<string, number>;
  /** 現在のラウンド番号 */
  roundNumber: number;
  /** ラウンド履歴 */
  roundHistory: RoundResult[];
  /** マッチ勝者（終了時のみ） */
  winner: Team | null;
  /** 敗北したプレイヤーID（持ち点0以下） */
  defeatedPlayerId: string | null;
}
